import { ApolloClient, gql } from 'apollo-boost';
import { ReportStage } from 'donor/portal/pages/FeedbackDashboard/api';
import {
  CustomQuestion,
  CustomSingleQuestion,
  CustomQuestionStructure,
  CustomQuestionSectionId,
  CustomQuestionType,
  AllReportStage,
} from './utilts';

const allPortfolioQuery = gql`
  query allPortfolios($filters: [PortofoliosFilter!]) {
    allPortfolios(filters: { and: $filters }) {
      edges {
        node {
          accountId
          portfolioId
          description
          name
          year
          years
          accountName
          customQuestions
          dbId
          status
        }
      }
    }
  }
`;

export type PortfolioYears = {
  id: number;
  investments: number;
  allocated: string; // '24.00'
  invested: string; // '24.00'
  is_main: boolean;
  manager: number; // 19822
  programs: number;
  report_date_1: string | null; // "2025-01-06"
  report_date_2: string | null;
  report_date_3: string | null;
  report_date_4: string | null;
  report_date_5: string | null;
  report_date_6: string | null;
  report_date_7: string | null;
  report_date_8: string | null;
  reports: number;
  reviewLevel: 'Grantee+TI' | string | null;
  services: (string | undefined)[];
  timeline_days_1: null;
  timeline_days_2: null;
  timeline_days_3: null;
  timeline_days_4: null;
  timeline_days_5: null;
  timeline_days_6: null;
  timeline_days_7: null;
  timeline_days_8: null;
  timeline_from_1: null;
  timeline_from_2: null;
  timeline_from_3: null;
  timeline_from_4: null;
  timeline_from_5: null;
  timeline_from_6: null;
  timeline_from_7: null;
  timeline_from_8: null;
  type: 'Grantee+Funder' | string | null;
  year: number; // 2024
}[];

export interface Portfolio {
  accountId: number;
  portfolioId: number;
  name: string;
  description: string;
  year: string; // "2016"
  years: string; // JSON formate
  accountName: string;
  customQuestions: null;
  dbId: number;
  status: 'ACTIVE';
}

export const getPortfoliosByAccountId = (
  client: ApolloClient<object>,
  accountId: number | string,
) => {
  const variables = {
    filters: [{ accountId }, { enabled: true }],
  };
  return client.query({
    query: allPortfolioQuery,
    variables,
    fetchPolicy: 'network-only',
  });
};

export const getPortfolioById = (
  client: ApolloClient<object>,
  id: number | string,
) => {
  const variables = {
    filters: [{ id }, { enabled: true }],
  };
  return client.query({
    query: allPortfolioQuery,
    variables,
    fetchPolicy: 'network-only',
  });
};

const saveSurveyQuery = gql`
  mutation reportSurvey(
    $id: Int
    $name: String!
    $description: String!
    $donorId: Int
    $portfolioId: Int
    $years: [String]
    $stage: [String]
  ) {
    reportSurvey(
      id: $id
      name: $name
      description: $description
      donorId: $donorId
      portfolioId: $portfolioId
      years: $years
      stage: $stage
    ) {
      id
      success
    }
  }
`;

const enableSurveyQuery = gql`
  mutation reportSurvey($id: Int!, $enabled: Boolean!) {
    reportSurvey(id: $id, enabled: $enabled) {
      id
      success
    }
  }
`;

export const allYearSurveyText = 'All Years';
export const saveSurveyApiRequest = (
  client: ApolloClient<object>,
  variables: {
    id?: number | string;
    name: string;
    description: string;
    donorId: number;
    stage: AllReportStage[];
    years: string[];
    portfolioId: number | undefined;
  },
) => {
  return client.mutate({
    mutation: saveSurveyQuery,
    variables,
    fetchPolicy: 'no-cache',
  });
};

export const enableSurveyApiRequest = (
  client: ApolloClient<object>,
  variables: {
    id: number | string;
    enabled: boolean;
  },
) => {
  return client.mutate({
    mutation: enableSurveyQuery,
    variables,
    fetchPolicy: 'no-cache',
  });
};

export type SurveyQuestionTypeFromAPI =
  | 'COMMENT'
  | 'TEXT'
  | 'INTEGER'
  | 'MULTIPLE_TEXT'
  | 'MULTIPLE_INTEGER'
  | 'DROPDOWN'
  | 'MATRIX_DROPDOWN'
  | 'RADIO_GROUP'
  | 'CHECKBOX'
  | 'INTRO';
export type SurveyQuestionSectionFromAPI =
  | 'DESCRIPTION'
  | 'DATES'
  | 'LOCATION'
  | 'IMPACT_SUMMARY'
  | 'IMPACT_DETAILS'
  | 'BENEFICIARIES'
  | 'NARRATIVE'
  | 'ACTIVITIES'
  | 'BUDGET'
  | 'PROGRESS_NOTES'
  | 'MISCELLANEOUS';
export type SurveyQuestionStateFromAPI = 'START' | 'MIDPOINT' | 'END';

export const mapCustomQuestionType = (
  type: CustomQuestionType,
): SurveyQuestionTypeFromAPI => {
  switch (type) {
    case 'comment':
      return 'COMMENT';
    case 'text':
      return 'TEXT';
    case 'integer':
      return 'INTEGER';
    case 'multi-text':
      return 'MULTIPLE_TEXT';
    case 'multi-integer':
      return 'MULTIPLE_INTEGER';
    case 'dropdown':
      return 'DROPDOWN';
    case 'matrix-dropdown':
      return 'MATRIX_DROPDOWN';
    case 'radio-group':
      return 'RADIO_GROUP';
    case 'checkbox':
      return 'CHECKBOX';
    case 'guidance':
      return 'INTRO';
  }
};

const mapQuestionStructure = (
  type: SurveyQuestionTypeFromAPI,
  options: SurveyQuestionFromAPI['options'],
): CustomQuestionStructure => {
  switch (type) {
    case 'COMMENT':
      return { questionType: 'comment', structure: undefined };
    case 'TEXT':
      return { questionType: 'text', structure: undefined };
    case 'INTEGER':
      return { questionType: 'integer', structure: undefined };
    case 'MULTIPLE_TEXT':
      return {
        questionType: 'multi-text',
        structure: options.map(x => ({ id: x.dbId, placeholder: x.value })),
      };
    case 'MULTIPLE_INTEGER':
      return {
        questionType: 'multi-integer',
        structure: options.map(x => ({ id: x.dbId, placeholder: x.value })),
      };
    case 'DROPDOWN':
      return {
        questionType: 'dropdown',
        structure: options.map(x => ({ id: x.dbId, label: x.value })),
      };
    case 'MATRIX_DROPDOWN':
      return { questionType: 'matrix-dropdown', structure: undefined };
    case 'RADIO_GROUP':
      return {
        questionType: 'radio-group',
        structure: options.map(x => ({ id: x.dbId, label: x.value })),
      };
    case 'CHECKBOX':
      return {
        questionType: 'checkbox',
        structure: options.map(x => ({ id: x.dbId, label: x.value })),
      };
    case 'INTRO':
      return {
        questionType: 'guidance',
        structure: [
          {
            guidanceText: options[0]?.text || '',
            questionId: options[0]?.value ? Number(options[0].value) : 0,
          },
        ],
      };
  }
};

export const mapSectionIdToCustomQuestionSection = (
  sectionId: CustomQuestionSectionId,
): SurveyQuestionSectionFromAPI => {
  switch (sectionId) {
    case 'description':
      return 'DESCRIPTION';
    case 'dates':
      return 'DATES';
    case 'location':
      return 'LOCATION';
    case 'impact_summary':
      return 'IMPACT_SUMMARY';
    case 'indicator_details':
      return 'IMPACT_DETAILS';
    case 'beneficiaries':
      return 'BENEFICIARIES';
    case 'narrative':
      return 'NARRATIVE';
    case 'budget':
      return 'BUDGET';
    case 'activities':
      return 'ACTIVITIES';
    default:
    case 'miscellaneous':
      return 'PROGRESS_NOTES';
  }
};

const mapSurveySection = (
  sectionId: SurveyQuestionSectionFromAPI,
): CustomQuestionSectionId => {
  switch (sectionId) {
    case 'DESCRIPTION':
      return 'description';
    case 'DATES':
      return 'dates';
    case 'LOCATION':
      return 'location';
    case 'IMPACT_SUMMARY':
      return 'impact_summary';
    case 'IMPACT_DETAILS':
      return 'indicator_details';
    case 'BENEFICIARIES':
      return 'beneficiaries';
    case 'NARRATIVE':
      return 'narrative';
    case 'BUDGET':
      return 'budget';
    case 'ACTIVITIES':
      return 'activities';
    case 'MISCELLANEOUS':
    case 'PROGRESS_NOTES':
      return 'miscellaneous';
  }
};

interface SurveyQuestionFromAPI {
  dbId: number;
  description: string;
  nextQuestionId: number | null;
  questionType: SurveyQuestionTypeFromAPI;
  required: boolean;
  section: SurveyQuestionSectionFromAPI;
  state: SurveyQuestionStateFromAPI;
  title: string;
  answers: {
    dbId: number;
    value: string;
    optionId: number;
  }[];
  options: {
    dbId: number;
    text: string;
    value: string;
  }[];
}

export interface SurveyFromAPI {
  dbId: number;
  donorId: number;
  name: string;
  portfolioId: number;
  description: string;
  enabled: boolean;
  portfolio?: Portfolio;
  years: string[];
  stage: AllReportStage[];
  questions: SurveyQuestionFromAPI[];
}

const getAllSurveysQuery = (
  portfolioId?: number | string,
  donorId?: number | string,
) => `
  query allReportSurvey {
    allReportSurvey(filters: {${
      portfolioId ? `portfolioId: ${portfolioId}` : ''
    } ${donorId ? `donorId: ${donorId}` : ''}}) {
      edges {
        node {
          dbId
          name
          description
          donorId
          portfolioId
          enabled
          years
          stage
          portfolio {
            portfolioId
            accountId
            accountName
            name
            description
            year
            years
            status
            customQuestions
          }
          questions {
            edges {
              node {
                dbId
                title
                description
                nextQuestionId
                required
                questionType
                state
                section
                options {
                  edges {
                    node {
                      dbId
                      text
                      value
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const mapSurveyAPIResponseQuestions = (
  surveyId: number | string,
  questions: SurveyQuestionFromAPI[],
): CustomSingleQuestion[] => {
  const result: CustomSingleQuestion[] = [];
  for (const q of questions) {
    const questionStructure = mapQuestionStructure(q.questionType, q.options);
    const sectionId = mapSurveySection(q.section);
    result.push({
      surveyId,
      dbId: q.dbId,
      questionStructure,
      questionText: q.title,
      questionType: questionStructure.questionType,
      sectionId,
      answer: q.answers,
      helperText: q.description,
    });
  }
  return result;
};

export const mapSurveyAPIResponse = (
  response: SurveyFromAPI[],
): CustomQuestion[] => {
  const result: CustomQuestion[] = [];
  for (const r of response) {
    const questions = mapSurveyAPIResponseQuestions(r.dbId, r.questions);
    result.push({
      dbId: r.dbId,
      description: r.description,
      enabled: r.enabled,
      portfolio: r.portfolio,
      portfolioId: r.portfolioId,
      questions,
      reportStage: r.stage,
      surveyName: r.name,
      years: r.years,
    });
  }
  return result;
};

export const getAllSurveys = (
  client: ApolloClient<object>,
  portfolioId: number | undefined,
  donorId: number | string | undefined,
) => {
  const variables = { portfolioId, donorId };
  return client.query<{ allReportSurvey: SurveyFromAPI[] }>({
    query: gql(getAllSurveysQuery(portfolioId, donorId)),
    variables: variables,
    fetchPolicy: 'network-only',
  });
};
